import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { PreApplicationJourney } from '../../../core/applications/pre-application.types';
import { SystemService } from '../../../core/admin/system.service';
import { IconComponent } from '../../../shared/icon/icon';
import { PreApplicationJourneyComponent } from '../../ops/pre-applications/detail/journey/journey';
import {
  BALL_LABEL,
  CHANNEL_LABEL,
  DOCUMENTS,
  EXCHANGES,
  NOT_SETTLED,
  PEOPLE,
  PRODUCT_PATHS,
  STAGES,
} from './user-journey.content';

/**
 * Explains how an application moves through Flow: whose move it is at each stage,
 * how Kickstart and Debtors Finance are reached, what passes between the business
 * and CapX, and the chart of every step and its rule.
 */
@Component({
  selector: 'app-user-journey',
  imports: [
    RouterLink,
    MatButtonModule,
    MatProgressSpinnerModule,
    IconComponent,
    PreApplicationJourneyComponent,
  ],
  templateUrl: './user-journey.html',
  styleUrl: './user-journey.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserJourneyComponent implements OnInit {
  private readonly systemService = inject(SystemService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly stages = STAGES;
  protected readonly ballLabel = BALL_LABEL;
  protected readonly paths = PRODUCT_PATHS;
  protected readonly exchanges = EXCHANGES;
  protected readonly channelLabel = CHANNEL_LABEL;
  protected readonly documents = DOCUMENTS;
  protected readonly people = PEOPLE;
  protected readonly notSettled = NOT_SETTLED;

  protected readonly selectedKey = signal(STAGES[0].key);
  protected readonly selected = computed(
    () => STAGES.find((s) => s.key === this.selectedKey()) ?? STAGES[0],
  );

  protected readonly journey = signal<PreApplicationJourney | null>(null);
  protected readonly chartError = signal(false);

  ngOnInit(): void {
    this.loadChart();
  }

  protected loadChart(): void {
    this.chartError.set(false);
    this.systemService
      .getJourneyReference()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (journey) => this.journey.set(journey),
        error: () => this.chartError.set(true),
      });
  }
}
